// Anmeldung — das Erste, was jeder sieht.
//
// Bewusst im CRM-Design-System (Input/Button/Field), NICHT mit den alten
// Shop-Klassen: die hatten ein hart weißes Eingabefeld und erbten die
// Schriftfarbe vom Body. Im dunklen Theme stand damit heller Text auf weißem
// Grund — praktisch unlesbar.

import { FormEvent, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/stores/authStore";
import type { Role } from "@/lib/supabase";
import { Button, Field, Input } from "@/design-system/ui";
import { useTheme, type ThemeMode } from "@/lib/theme";

function landingFor(role: Role): string {
  // Personal landet im CRM — das ist die App, mit der der Hof arbeitet.
  if (role === "kunde_privat" || role === "kunde_edeka") return "/shop";
  return "/crm";
}

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await signIn(email, password);
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    const target = from && from !== "/login" ? from : landingFor(useAuth.getState().role);
    navigate(target, { replace: true });
  }

  // Seit das Dokument nicht mehr scrollt (index.css: body overflow hidden),
  // braucht JEDE Seite ihren eigenen Scrollbereich — sonst ist der Inhalt auf
  // kleinen Geräten oder bei offener Tastatur schlicht abgeschnitten, ohne dass
  // man drankommt.
  return (
    <div
      style={{ height: "calc(100% - var(--tastatur, 0px))" }}
      className="grid place-items-center overflow-y-auto bg-ink-950 px-4 py-10 text-ink-100 antialiased"
    >
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <form onSubmit={onSubmit} className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <img src="/icon-512.png" alt="" className="h-20 w-20 rounded-2xl shadow-lg" />
          <div>
            <h1 className="text-2xl font-semibold text-ink-50">Hof Sirges</h1>
            <p className="mt-1 text-sm text-ink-400">Kunden · Touren · Auslieferung</p>
          </div>
        </div>

        <div className="space-y-4">
          <Field label="E-Mail">
            <Input
              type="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>

          <Field label="Passwort">
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>

          {error && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3.5 py-2.5 text-sm text-danger-fg">
              {error}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={submitting}
          >
            Anmelden
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Auch hier schon umschaltbar — sonst müsste man sich erst blenden lassen. */
function ThemeToggle() {
  const { mode, setTheme } = useTheme();
  const next: Record<ThemeMode, ThemeMode> = { system: "light", light: "dark", dark: "system" };
  const label = mode === "system" ? "System" : mode === "light" ? "Hell" : "Dunkel";

  return (
    <button
      onClick={() => setTheme(next[mode])}
      aria-label={`Theme umschalten — aktuell ${label}`}
      className="flex h-11 w-11 items-center justify-center rounded-lg text-ink-400 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
    >
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        {mode === "light" ? (
          <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </>
        ) : mode === "dark" ? (
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8" />
        ) : (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none" />
          </>
        )}
      </svg>
    </button>
  );
}
